import { el, clear } from "../components/dom.js";
import { shareCollection, stopSharing } from "../db/share.js";
import { showToast } from "../components/toast.js";
import { facetValueLabel } from "../utils/filters.js";
import {
	getGroupById,
	listGroups,
	resolveGroupMembersSync,
	subtreeMembers,
	summarizeMembers,
	deleteGroup,
	addItemToGroup,
	addChildGroup,
	removeChildGroup,
	createSubgroup,
	updateGroup,
} from "../db/groups.js";
import { listAllMedia } from "../db/media.js";
import { awardLabel, statusLabel } from "../components/status-badge.js";
import { listPicker } from "../components/picker.js";
import { enableRowDrag } from "../utils/drag-drop.js";
import { navigate, navigateBack } from "../main.js";
import {
	MAG,
	magGlass,
	magGlassSquare,
	magButton,
	magFieldLabel,
	magStatStrip,
	magEmpty,
	magEpisodeRow,
} from "../components/magazine/kit.js";
import { magShelf, fannedSpines } from "../components/magazine/shelf.js";
import { magDeleteFooter } from "../components/magazine/detail.js";
import { getBookSeries } from "../api/books.js";
import { getGameSeries } from "../api/igdb.js";
import { addSeriesBookToGroup } from "./book-series.js";
import { addSeriesGameToGroup } from "./games-series.js";

// A group has no status of its own — it's an overview of its members. This maps
// the group's scope to the token statusLabel wants for media-aware wording
// ("videogame" → default game labels → null).
function labelMediaType(group) {
	if (group.media_type === "book") return "book";
	if (group.media_type === "tv") return "tv";
	if (group.media_type === "anime") return "anime";
	if (group.media_type === "movie") return "movie";
	if (group.media_type === "comic") return "comic";
	return null;
}

export async function renderGroupDetail(root, id) {
	const group = await getGroupById(id);
	if (!group) {
		root.append(
			el(
				"div.view",
				{},
				el("p", {}, "Group not found."),
				el(
					"button.btn",
					{ type: "button", onclick: () => navigateBack("/groups") },
					"Back",
				),
			),
		);
		return;
	}
	const view = el("div.view", {});
	root.append(view);
	await mount(view, group);
}

async function mount(view, group) {
	clear(view);

	const [allMedia, allGroups] = await Promise.all([
		listAllMedia(),
		listGroups(),
	]);
	// Stats roll up the whole subtree; the member shelf below is shown folder-style
	// (subgroups + the loose items not already inside one of them).
	const members = subtreeMembers(group, allGroups, allMedia);
	const directMembers = resolveGroupMembersSync(group, allMedia);
	const childGroups = (group.child_group_ids ?? [])
		.map((cid) => allGroups.find((g) => g.id === cid))
		.filter(Boolean);
	const childMemberIds = new Set();
	for (const child of childGroups) {
		for (const m of subtreeMembers(child, allGroups, allMedia)) {
			childMemberIds.add(m.id);
		}
	}
	const looseMembers = directMembers.filter((m) => !childMemberIds.has(m.id));
	const summary = summarizeMembers(members);
	const mediaInfo = collectMediaInfo(members);
	const labelType = labelMediaType(group);

	// ---- compact box-set header (members lead; the shelf bird's-eye, not stats) ----
	const doneCount = summary.byEngagement.done ?? summary.doneCount ?? 0;
	const headerMetaBits = [`${summary.total} item${summary.total === 1 ? "" : "s"}`];
	if (summary.total > 0 && doneCount > 0) {
		const doneWord = statusLabel("done", labelType).toLowerCase();
		headerMetaBits.push(`${doneCount}/${summary.total} ${doneWord}`);
	}
	if (mediaInfo.mediaTypeLabel) headerMetaBits.push(mediaInfo.mediaTypeLabel);
	// Smart collection (roadmap #56): say what fills it.
	const smartLine = group.smart_rule?.filters?.length
		? el("div", { style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "10.5px", color: MAG.yellow, marginTop: "6px" } },
			`★ Smart · ${group.smart_rule.filters.map((f) => facetValueLabel(f, group.smart_rule.media_type)).join(" + ")}`)
		: "";
	// Progress through the collection (roadmap #57).
	const progressLine = summary.total > 1
		? el("div", { style: { height: "5px", borderRadius: "5px", background: "rgba(255,255,255,.12)", overflow: "hidden", marginTop: "8px" } },
			el("div", { style: { width: `${Math.round((doneCount / summary.total) * 100)}%`, height: "100%", background: MAG.lime } }))
		: "";

	const header = magGlass(
		el(
			"div",
			{ style: { display: "flex", alignItems: "center", gap: "14px" } },
			fannedSpines(members, { height: "54px", width: "38px" }),
			el(
				"div",
				{ style: { flex: "1", minWidth: "0" } },
				el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9px",
						letterSpacing: ".12em", textTransform: "uppercase", color: MAG.lime, marginBottom: "3px",
					},
				}, "Collection"),
				el("div", {
					style: {
						fontFamily: "'Anton', sans-serif", fontSize: "30px", lineHeight: ".9",
						textTransform: "uppercase", color: "#fff",
					},
				}, group.name),
				el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "10px",
						letterSpacing: ".05em", textTransform: "uppercase", color: MAG.dim, marginTop: "6px",
					},
				}, headerMetaBits.join("  ·  ")),
				smartLine,
				progressLine,
			),
		),
		{
			style: {
				display: "flex", alignItems: "center", gap: "12px", padding: "16px 16px",
				margin: "12px 0",
			},
		},
	);
	const headerRow = el(
		"div",
		{ style: { display: "flex", alignItems: "stretch", gap: "10px" } },
		el("div", { style: { paddingTop: "12px", flex: "none" } },
			magGlassSquare("‹", { onClick: () => navigateBack("/groups"), label: "Back" })),
		el("div", { style: { flex: "1", minWidth: "0" } }, header),
	);

	// ---- demoted, collapsible stat strip (Items / Done / Avg rating / Have it) ----
	const statStrip = magStatStrip(
		[
			{ value: summary.total, label: "Items" },
			{ value: doneCount, label: "Done" },
			{ value: summary.avgRating != null ? `${summary.avgRating.toFixed(1)}★` : "—", label: "Avg rating" },
			{ value: summary.ownedCount, label: "Have it" },
		],
		{ collapsible: true },
	);

	// Engagement + awards rollups — secondary detail, kept under the strip.
	const engagementRows = Object.entries(summary.byEngagement)
		.sort(([, a], [, b]) => b - a)
		.map(([key, count]) => ({ label: statusLabel(key, labelType), count }));
	const breakdown =
		engagementRows.length > 0 ? statsSection("Engagement", engagementRows) : null;

	const awardEntries = Object.entries(summary.awardCounts ?? {}).sort(
		([, a], [, b]) => b - a,
	);
	const awardsOverview =
		awardEntries.length > 0
			? el(
					"div",
					{ style: { marginTop: "12px" } },
					magFieldLabel("Awards", "#ff3d8b"),
					el(
						"div",
						{ style: { display: "flex", flexWrap: "wrap", gap: "7px" } },
						...awardEntries.map(([value, count]) =>
							el("span", {
								style: {
									fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "10px",
									letterSpacing: ".04em", textTransform: "uppercase", color: MAG.lime,
									border: "1px solid rgba(182,242,61,.5)", background: "rgba(182,242,61,.08)",
									borderRadius: "999px", padding: "6px 11px",
								},
							}, `${awardLabel(value)} ×${count}`),
						),
					),
				)
			: null;

	const statsBlock = el(
		"div",
		{ style: { margin: "4px 0 6px" } },
		statStrip,
		breakdown ?? "",
		awardsOverview ?? "",
	);

	// Persist a patch to the group and re-render from the fresh record (used by
	// the cover picker and pin toggle — group metadata, not member status).
	const save = async (patch) => {
		await updateGroup(group.id, patch);
		await mount(view, await getGroupById(group.id));
	};

	// ---- the members SHELF (dense 3-col cover grid; subgroups as box-set tiles) ----
	const subgroupsForShelf = childGroups.map((child) => ({
		group: child,
		members: subtreeMembers(child, allGroups, allMedia),
	}));
	const hasEntries = childGroups.length > 0 || looseMembers.length > 0;

	const shelf = magShelf(looseMembers, {
		ordered: !!group.is_ordered,
		onTile: (m) => navigate(m.route),
		subgroups: subgroupsForShelf,
		onSubgroup: (child) => navigate(`/groups/${child.id}`),
	});

	// Reorder affordance: a hint, plus (once a custom order exists) a way back to
	// alphabetical. Both update in place after a drag so we needn't re-mount. The
	// shelf tiles carry data-drag-id/-kind="item"; subgroup box-set tiles don't, so
	// currentOrder() reads back only the loose members — exactly as before.
	const orderHint = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "11px",
			color: MAG.dim, lineHeight: "1.4",
		},
	}, "");
	const sortAzBtn = magButton("Sort A–Z", {
		variant: "ghost",
		onClick: async () => {
			await updateGroup(group.id, { is_ordered: false, ordered_ids: null });
			await mount(view, await getGroupById(group.id));
		},
	});
	function refreshOrderControls() {
		orderHint.textContent = group.is_ordered
			? "Custom order — long-press and drag tiles to rearrange."
			: "Long-press and drag a tile to set a custom order.";
		sortAzBtn.hidden = !group.is_ordered;
	}
	const orderControls = el(
		"div",
		{ style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", margin: "0 0 10px" } },
		orderHint,
		sortAzBtn,
	);
	if (looseMembers.length > 1) {
		refreshOrderControls();
		// Attach the reorder controller to the shelf grid; only member tiles carry
		// data-drag-id, so reorder commits the loose-member order back.
		enableRowDrag(shelf, {
			onReorder: async (ids) => {
				group.is_ordered = true;
				group.ordered_ids = ids;
				await updateGroup(group.id, { is_ordered: true, ordered_ids: ids });
				refreshOrderControls();
			},
		});
	}

	const membersSection = el(
		"div",
		{ style: { marginTop: "8px" } },
		magFieldLabel("Members", MAG.lime),
		looseMembers.length > 1 ? orderControls : "",
		hasEntries
			? shelf
			: magEmpty({ title: "Nothing here yet", subtitle: "Add items below to start your shelf." }),
	);

	// ---- series suggestions: the rest of the series, not yet added ----
	// For collections created from a book/game series (series_ref set). Filled
	// asynchronously (an external lookup) so the collection renders immediately;
	// best-effort, hidden if everything's already in.
	let seriesSection = "";
	if (group.series_ref) {
		const seriesContainer = el("div", { style: { marginTop: "20px" } });
		seriesSection = seriesContainer;
		(async () => {
			try {
				const isGame = group.media_type === "videogame";
				// Normalize both sources to { title, cover_url, year, ordinal, raw }.
				let entries;
				if (isGame) {
					const id = group.series_ref.replace(/^igdb:/, "");
					const series = await getGameSeries(id);
					entries = (series.games ?? []).map((g) => ({
						title: g.name, cover_url: g.cover_url, year: g.release_year, ordinal: null, raw: g,
					}));
				} else {
					const series = await getBookSeries(group.series_ref);
					entries = (series.books ?? []).map((b) => ({
						title: b.title, cover_url: b.cover_url, year: b.published_year, ordinal: b.ordinal, raw: b,
					}));
				}
				const have = new Set(members.map((m) => (m.title ?? "").trim().toLowerCase()));
				const missing = entries.filter((e) => !have.has((e.title ?? "").trim().toLowerCase()));
				if (missing.length === 0) return;

				clear(seriesContainer);
				seriesContainer.append(magFieldLabel("From the series — not yet added", "#ff7a1a"));
				missing.forEach((e, i) => {
					seriesContainer.append(
						magEpisodeRow(
							{ title: e.title, cover_url: e.cover_url, media_type: group.media_type },
							{
								number: e.ordinal ?? i + 1,
								kicker: e.year ? String(e.year) : "Suggestion",
								owned: false,
								addLabel: "+ Add",
								dimmed: true,
								onAdd: async () => {
									if (isGame) {
										const id = group.series_ref.replace(/^igdb:/, "");
										await addSeriesGameToGroup({ collection_id: Number(id), title: group.name }, e.raw);
									} else {
										await addSeriesBookToGroup({ wd_id: group.series_ref, title: group.name }, e.raw);
									}
									await mount(view, await getGroupById(group.id));
								},
							},
						),
					);
				});
			} catch {
				// Suggestions are best-effort — leave the section empty on failure.
			}
		})();
	}

	// ---- add-item picker ----
	const memberIds = new Set(members.map((m) => m.id));
	const candidates = allMedia
		.filter((m) => !memberIds.has(m.id))
		.sort((a, b) => (a.title ?? "").localeCompare(b.title ?? ""));

	const picker = listPicker({
		items: candidates.map((m) => ({
			id: m.id,
			name: m.title,
			subtitle: m.secondary ? `${m.mediaLabel} · ${m.secondary}` : m.mediaLabel,
		})),
		placeholder: "Search your library…",
		emptyText: "Everything in your library is already in this group.",
		onPick: async (item) => {
			await addItemToGroup(group.id, item.id);
			const fresh = await getGroupById(group.id);
			await mount(view, fresh);
		},
	});

	const addBtn = magButton("+ Add an item", {
		variant: "primary",
		onClick: () => {
			picker.hidden = !picker.hidden;
			if (!picker.hidden) {
				const input = picker.querySelector(".picker__input");
				if (input) queueMicrotask(() => input.focus());
			}
		},
	});
	const addSection = el(
		"div",
		{ style: { margin: "16px 0 0" } },
		addBtn,
		picker,
	);

	// ---- subgroup management (create / nest / detach) ----
	const subgroupSection = renderSubgroupControls(group, childGroups, subgroupsForShelf, allGroups, view);

	// ---- group settings: rename, cover, pin to home ----
	const renameInput = el("input", {
		value: group.name,
		type: "text",
		style: {
			width: "100%", boxSizing: "border-box", background: "transparent", border: "0",
			outline: "none", color: "#fff",
			fontFamily: "'Anton', sans-serif", fontSize: "18px", textTransform: "uppercase",
		},
	});
	renameInput.addEventListener("change", async () => {
		const v = renameInput.value.trim();
		if (!v || v === group.name) return;
		await updateGroup(group.id, { name: v });
		const fresh = await getGroupById(group.id);
		await mount(view, fresh);
	});
	const renameField = el(
		"div",
		{},
		magFieldLabel("Name", "#19d3e8"),
		magGlass(renameInput, { style: { padding: "12px 14px" } }),
	);

	const coverField =
		members.length > 0
			? renderCoverField(group, members, save)
			: "";

	// Pin-to-home toggle. Off by default; pinning surfaces the group as a card on
	// the home screen.
	const pinned = !!group.pinned_to_home;
	const pinBtn = magButton(pinned ? "✓ Pinned to home" : "Pin to home", {
		variant: pinned ? "owned" : "ghost",
		block: true,
		onClick: async () => {
			await updateGroup(group.id, { pinned_to_home: !pinned });
			const fresh = await getGroupById(group.id);
			await mount(view, fresh);
		},
	});
	const pinField = el(
		"div",
		{},
		magFieldLabel("Home screen", "#ffd60a"),
		pinBtn,
	);

	const settingsSection = el(
		"div",
		{ style: { display: "flex", flexDirection: "column", gap: "4px", marginTop: "20px" } },
		renameField,
		coverField,
		pinField,
	);

	const footer = magDeleteFooter("Delete collection", async () => {
		if (
			!confirm(
				`Delete group "${group.name}"? The items stay; just the grouping goes away.`,
			)
		)
			return;
		await deleteGroup(group.id);
		navigate("/groups");
	});

	// Share as a read-only link (roadmap #58).
	const shareRow = el("div", { style: { display: "flex", gap: "8px", flexWrap: "wrap", margin: "0 0 12px" } });
	const paintShare = (g) => {
		shareRow.replaceChildren(
			magButton(g.share_token ? "Update shared link" : "Share as a link", {
				variant: "ghost",
				onClick: async () => {
					try {
						const url = await shareCollection(g, members);
						g = await getGroupById(g.id);
						paintShare(g);
						try {
							if (navigator.share) await navigator.share({ title: g.name, url });
							else {
								await navigator.clipboard.writeText(url);
								showToast("Link copied — anyone with it can see this collection.");
							}
						} catch (err) {
							if (err?.name !== "AbortError") showToast(url);
						}
					} catch (err) {
						showToast(err?.message ?? "Couldn't share.");
					}
				},
			}),
			g.share_token
				? magButton("Stop sharing", {
						variant: "ghost",
						onClick: async () => {
							try {
								await stopSharing(g);
								g = await getGroupById(g.id);
								paintShare(g);
								showToast("The link no longer works.");
							} catch (err) {
								showToast(err?.message ?? "Couldn't stop sharing.");
							}
						},
					})
				: "",
		);
	};
	paintShare(group);

	view.append(
		headerRow,
		shareRow,
		statsBlock,
		membersSection,
		seriesSection,
		addSection,
		subgroupSection,
		settingsSection,
		footer,
	);
}

// All group ids in a group's subtree (inclusive), for cycle-aware filtering.
function subtreeIds(rootId, byId, acc = new Set()) {
	if (acc.has(rootId)) return acc;
	acc.add(rootId);
	for (const cid of byId.get(rootId)?.child_group_ids ?? []) {
		subtreeIds(cid, byId, acc);
	}
	return acc;
}

// Subgroup management: detach existing children (keeps the group, lifts it back to
// top level), plus controls to create a new subgroup or nest an existing one. The
// subgroups themselves render as box-set tiles inside the shelf; this section is
// just their controls.
function renderSubgroupControls(group, childGroups, subgroupsForShelf, allGroups, view) {
	const blocks = [magFieldLabel("Subgroups", "#8b3dff")];

	// Detach rows — one per current child.
	for (const sg of subgroupsForShelf) {
		const child = sg.group;
		blocks.push(
			magGlass(
				el(
					"div",
					{ style: { display: "flex", alignItems: "center", gap: "12px" } },
					el("div", { style: { flex: "1", minWidth: "0" } },
						el("div", {
							style: {
								fontFamily: "'Anton', sans-serif", fontSize: "16px", lineHeight: ".95",
								textTransform: "uppercase", color: "#fff",
								overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
							},
						}, child.name),
						el("div", {
							style: {
								fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9px",
								letterSpacing: ".06em", textTransform: "uppercase", color: MAG.dim, marginTop: "3px",
							},
						}, `${sg.members.length} inside`),
					),
					magButton("Detach", {
						variant: "ghost",
						onClick: async () => {
							await removeChildGroup(group.id, child.id);
							await mount(view, await getGroupById(group.id));
						},
					}),
				),
				{ style: { display: "flex", alignItems: "center", padding: "10px 12px", marginBottom: "6px" } },
			),
		);
	}

	const byId = new Map(allGroups.map((g) => [g.id, g]));
	const currentChildren = new Set(group.child_group_ids ?? []);
	const candidates = allGroups
		.filter((g) => {
			if (g.id === group.id) return false;
			if (currentChildren.has(g.id)) return false;
			// Exclude ancestors: a group whose subtree already contains this group.
			return !subtreeIds(g.id, byId).has(group.id);
		})
		.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

	const createBtn = el(
		"button.btn.btn--small.picker__create",
		{
			type: "button",
			onclick: async () => {
				const name = prompt("New subgroup name:");
				if (!name?.trim()) return;
				const child = await createSubgroup(group.id, { name: name.trim() });
				navigate(`/groups/${child.id}`);
			},
		},
		"+ Create new subgroup",
	);

	const picker = listPicker({
		items: candidates.map((g) => ({
			id: g.id,
			name: g.name,
			subtitle: `${(g.manual_member_ids ?? []).length} item${
				(g.manual_member_ids ?? []).length === 1 ? "" : "s"
			}`,
		})),
		placeholder: "Search your groups…",
		emptyText:
			allGroups.length <= 1
				? "No other groups yet — create one below."
				: "No eligible groups to nest here.",
		extraAction: createBtn,
		onPick: async (item) => {
			try {
				await addChildGroup(group.id, item.id);
			} catch (err) {
				alert(err.message);
				return;
			}
			await mount(view, await getGroupById(group.id));
		},
	});

	const toggleBtn = magButton("+ Add a subgroup", {
		variant: "ghost",
		onClick: () => {
			picker.hidden = !picker.hidden;
			if (!picker.hidden) {
				const input = picker.querySelector(".picker__input");
				if (input) queueMicrotask(() => input.focus());
			}
		},
	});

	blocks.push(toggleBtn, picker);
	return el("div", { style: { marginTop: "20px" } }, ...blocks);
}

function collectMediaInfo(members) {
	const mediaTypes = new Set();
	const platforms = new Set();
	for (const g of members) {
		if (g.media_type) mediaTypes.add(g.media_type);
		// Prefer the platforms the user owns it on; fall back to release
		// platforms so brand-new groups still show *something* useful.
		const list =
			(g.owned_on && g.owned_on.length > 0
				? g.owned_on
				: g.platforms) ?? [];
		for (const p of list) platforms.add(p);
	}
	const MEDIA_LABELS = { videogame: "Videogames", book: "Books" };
	let mediaTypeLabel = null;
	if (mediaTypes.size === 1) {
		const only = Array.from(mediaTypes)[0];
		mediaTypeLabel = MEDIA_LABELS[only] ?? only;
	} else if (mediaTypes.size > 1) {
		mediaTypeLabel = "Mixed media";
	}
	return {
		mediaTypeLabel,
		platforms: Array.from(platforms).sort(),
	};
}

// Cover picker: a searchable list of members; picking one sets cover_item_id.
function renderCoverField(group, members, save) {
	const current = members.find((m) => m.id === group.cover_item_id);
	const currentLabel = current ? current.title : "First member with art";

	const picker = listPicker({
		items: members.map((m) => ({
			id: m.id,
			name: m.title,
			subtitle: m.mediaLabel,
		})),
		placeholder: "Choose a cover from the members…",
		emptyText: "No members to choose from.",
		onPick: (item) => save({ cover_item_id: item.id }),
	});

	const toggleBtn = magButton("Change cover", {
		variant: "ghost",
		onClick: () => {
			picker.hidden = !picker.hidden;
			if (!picker.hidden) {
				const input = picker.querySelector(".picker__input");
				if (input) queueMicrotask(() => input.focus());
			}
		},
	});

	return el(
		"div",
		{},
		magFieldLabel("Cover", "#ff7a1a"),
		el("div", {
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "11px",
				color: MAG.dim, margin: "0 0 8px",
			},
		}, `Using: ${currentLabel}`),
		toggleBtn,
		picker,
	);
}

// A small horizontal-bar engagement breakdown, reskinned in magazine glass.
function statsSection(title, rows) {
	const max = Math.max(...rows.map((r) => r.count), 1);
	return el(
		"div",
		{ style: { marginTop: "12px" } },
		magFieldLabel(title, "#19d3e8"),
		magGlass(
			el(
				"div",
				{ style: { display: "flex", flexDirection: "column", gap: "8px" } },
				...rows.map((row) =>
					el(
						"div",
						{ style: { display: "flex", alignItems: "center", gap: "10px" } },
						el("div", {
							style: {
								fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "10px",
								letterSpacing: ".04em", textTransform: "uppercase", color: MAG.dim,
								width: "90px", flex: "none",
								overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
							},
						}, row.label),
						el(
							"div",
							{ style: { flex: "1", height: "8px", borderRadius: "999px", background: "rgba(255,255,255,.08)", overflow: "hidden" } },
							el("div", {
								style: { height: "100%", width: `${(row.count / max) * 100}%`, background: MAG.lime, borderRadius: "999px" },
							}),
						),
						el("div", {
							style: { fontFamily: "'Anton', sans-serif", fontSize: "14px", color: "#fff", width: "24px", textAlign: "right", flex: "none" },
						}, String(row.count)),
					),
				),
			),
			{ style: { padding: "12px 14px" } },
		),
	);
}
